import { useEffect, useState } from 'react';
import { ref, getBytes } from 'firebase/storage';
import { storage, ensureAppCheckToken } from '../firebase/firebase';

/**
 * Loads a Storage PDF into a blob URL for react-pdf (avoids detached ArrayBuffer in workers).
 * @param {string | null} storagePath
 */
export default function useGlobalStoragePdfPreview(storagePath) {
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!storagePath) return undefined;

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    (async () => {
      try {
        const appCheck = await ensureAppCheckToken(true);
        if (!appCheck.ok) {
          if (!cancelled) {
            setPreviewPdfUrl((prev) => {
              if (prev) URL.revokeObjectURL(prev);
              return null;
            });
            setLoadError(appCheck.errorMessage || 'App Check failed — cannot load PDF.');
          }
          return;
        }
        const bytes = await getBytes(ref(storage, storagePath));
        if (cancelled) return;
        const blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPreviewPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
        setLoadError(null);
      } catch (e) {
        if (!cancelled) {
          setPreviewPdfUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
          });
          setLoadError(e?.message || 'Could not load PDF from Storage.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storagePath]);

  useEffect(() => {
    return () => {
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  return { previewPdfUrl, loadError, loading };
}
